import React, { useMemo, useState } from 'react'

import AnimationLink from '../Link'
import LetsTalkCta from '../LetsTalkCta'
import arrowSmallWhite from '../../assets/images/arrow-small-white.svg'
import { getWorkShowcaseEntries } from '../../constants/workShowcase'

/** Icon inside 40px circle (see `.home-work-showcase__card-arrow img`) */
const CARD_ARROW_PX = 22

const FILTER_ALL = 'all'

/** Pre-production: set true when project detail / in-page anchors are ready. */
const WORK_PROJECT_CARDS_LINKED = false

/** Same icon alternation as homepage showcase tags; renders all project tags. */
const WorkProjectsShowcaseTags = ({ items }) => {
  if (!items || !items.length) return null
  return (
    <ul className="home-work-showcase__tags work-projects-showcase__tags-list">
      {items.map((label, i) => (
        <li key={`${label}-${i}`} className="home-work-showcase__tag">
          <span
            className={
              i % 2 === 0
                ? 'home-work-showcase__tag-icon home-work-showcase__tag-icon--circle'
                : 'home-work-showcase__tag-icon home-work-showcase__tag-icon--triangle'
            }
            aria-hidden
          />
          <span className="home-work-showcase__tag-label">{label}</span>
        </li>
      ))}
    </ul>
  )
}

const WorkProjectCardBody = ({ project }) => (
  <>
    <span className="home-work-showcase__card-hover-frame" aria-hidden="true" />
    <div className="home-work-showcase__card-inner">
      <div className="home-work-showcase__card-media">
        <img
          src={project.image}
          alt={project.title}
          loading={project.loading || 'lazy'}
          decoding="async"
          className="home-work-showcase__card-img"
        />
      </div>
      <div className="home-work-showcase__card-panel">
        <div className="home-work-showcase__card-heading">
          <h3 className="home-work-showcase__card-title">{project.title}</h3>
          <span className="home-work-showcase__card-arrow" aria-hidden="true">
            <img
              src={arrowSmallWhite}
              alt=""
              width={CARD_ARROW_PX}
              height={CARD_ARROW_PX}
              decoding="async"
            />
          </span>
        </div>
        <div className="home-work-showcase__card-rule" aria-hidden="true" />
        <p className="home-work-showcase__card-desc">{project.description}</p>
        <WorkProjectsShowcaseTags items={project.workTypes.items} />
      </div>
    </div>
  </>
)

const WorkProjectCard = ({ project, workBase, linked }) => {
  if (linked) {
    return (
      <AnimationLink
        className="home-work-showcase__card"
        hex="#322EFF"
        paintDrip
        to={`${workBase}#${project.id}`}
        id={`#${project.id}`}
      >
        <WorkProjectCardBody project={project} />
      </AnimationLink>
    )
  }

  return (
    <article className="home-work-showcase__card">
      <WorkProjectCardBody project={project} />
    </article>
  )
}

const WorkProjectsShowcase = ({ lang }) => {
  const isEn = lang === 'en'
  const projects = useMemo(() => getWorkShowcaseEntries(isEn), [isEn])
  const workBase = isEn ? '/en/work/' : '/trabalho/'

  const filterPromptLabel = isEn ? 'Show Projects:' : 'Mostrar Projetos:'
  const allOptionLabel = isEn ? 'All Areas' : 'Todas as áreas'
  const emptyLabel = isEn ? 'No projects match this filter.' : 'Nenhum projeto corresponde a este filtro.'
  const filterSelectId = 'work-projects-showcase-filter'
  const ctaTitle = isEn ? 'Have a Project in Mind?' : 'Tem um projeto em mente?'
  const ctaBody = isEn
    ? 'Share your project with us — from digital products and software solutions to e-commerce and tech integrations.'
    : 'Partilhe o seu projeto connosco — de produtos digitais e soluções de software a e-commerce e integrações tecnológicas.'

  const tagOptions = useMemo(() => {
    const seen = new Set()
    projects.forEach(p => {
      ;(p.workTypes?.items ?? []).forEach(t => {
        if (t) seen.add(t)
      })
    })
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  }, [projects])

  const [activeFilter, setActiveFilter] = useState(FILTER_ALL)

  const visibleProjects = useMemo(() => {
    if (activeFilter === FILTER_ALL) return projects
    return projects.filter(p => (p.workTypes?.items ?? []).includes(activeFilter))
  }, [projects, activeFilter])

  return (
    <section id="work-projects-showcase" className="bg-blue work-projects-showcase">
      <div className="work-projects-showcase__anchor-tray" aria-hidden="true">
        {projects.map(p => (
          <span key={`at-${p.id}`} id={p.id} className="work-projects-showcase__id-target" />
        ))}
      </div>
      <div className="content container">
        <div className="row work-projects-showcase__top">
          <div className="col-12 work-projects-showcase__filter-col">
            <div className="work-projects-showcase__filter-bar">
              <label htmlFor={filterSelectId} className="work-projects-showcase__filter-label">
                {filterPromptLabel}
              </label>
              <div className="work-projects-showcase__select-wrap">
                <select
                  id={filterSelectId}
                  className="work-projects-showcase__select"
                  value={activeFilter}
                  onChange={e => setActiveFilter(e.target.value)}
                >
                  <option value={FILTER_ALL}>{allOptionLabel}</option>
                  {tagOptions.map(tag => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="col-12">
            {visibleProjects.length === 0 ? (
              <p className="work-projects-showcase__empty" role="status">
                {emptyLabel}
              </p>
            ) : (
              <div className="home-work-showcase__grid work-projects-showcase__grid">
                {visibleProjects.map(project => (
                  <WorkProjectCard
                    key={project.id}
                    project={project}
                    workBase={workBase}
                    linked={WORK_PROJECT_CARDS_LINKED}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="col-12 work-projects-showcase__cta-col">
            <div className="work-projects-showcase__cta-card">
              <h2 className="work-projects-showcase__cta-title">{ctaTitle}</h2>
              <p className="work-projects-showcase__cta-text">{ctaBody}</p>
              <div className="work-projects-showcase__cta-actions">
                <LetsTalkCta isEn={isEn} hex="#322EFF" tone="accent" paintDrip />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default WorkProjectsShowcase
